/**
 * Timeline 配置块 JSON Schema 定义
 */
export const timelineSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/timeline.json',
  title: 'Timeline 配置',
  description: 'Timeline（时间线）配置数据结构',
  type: 'object',
  required: ['timeline_id', 'name'],
  properties: {
    timeline_id: {
      type: 'string',
      title: 'Timeline ID',
      description: 'Timeline 唯一标识符',
      pattern: '^timeline_[a-zA-Z0-9_]+$',
      minLength: 10,
      maxLength: 50,
      examples: ['timeline_main', 'timeline_chapter_1', 'timeline_side_story'],
    },
    name: {
      type: 'string',
      title: '时间线名称',
      description: '时间线的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['主线时间线', '第一章', '支线剧情'],
    },
    description: {
      type: 'string',
      title: '时间线描述',
      description: '时间线的详细描述',
      minLength: 0,
      maxLength: 500,
    },
    events: {
      type: 'array',
      title: '事件列表',
      description: '时间线上的所有事件',
      items: {
        type: 'object',
        required: ['event_id', 'trigger_time'],
        properties: {
          event_id: {
            type: 'string',
            title: '事件 ID',
            pattern: '^event_[a-zA-Z0-9_]+$',
          },
          event_name: {
            type: 'string',
            title: '事件名称',
            maxLength: 100,
          },
          trigger_time: {
            type: 'integer',
            title: '触发时间',
            description: '事件触发的时间点（秒）',
            minimum: 0,
          },
          event_type: {
            type: 'string',
            title: '事件类型',
            enum: ['对话', '剧情', '战斗', '选择', '环境变化', '任务触发', '其他'],
          },
          target: {
            type: 'string',
            title: '目标对象',
            description: '事件作用的目标（NPC ID, Location ID 等）',
          },
          condition: {
            type: 'string',
            title: '触发条件',
            description: '事件触发的额外条件表达式',
          },
          duration: {
            type: 'integer',
            title: '持续时间',
            description: '事件持续时间（秒）',
            minimum: 0,
          },
          priority: {
            type: 'integer',
            title: '优先级',
            description: '同时触发时的优先级',
            minimum: 0,
            maximum: 100,
            default: 50,
          },
          skippable: {
            type: 'boolean',
            title: '可跳过',
            description: '玩家是否可以跳过此事件',
            default: false,
          },
          effects: {
            type: 'array',
            title: '效果',
            description: '事件触发的效果列表',
            items: {
              type: 'object',
              properties: {
                effect_type: {
                  type: 'string',
                  enum: ['变量修改', '场景切换', 'NPC 状态变化', '物品获取', '其他'],
                },
                target: {
                  type: 'string',
                  title: '效果目标',
                },
                value: {
                  title: '效果值',
                  oneOf: [
                    { type: 'string' },
                    { type: 'number' },
                    { type: 'boolean' },
                    { type: 'object' },
                  ],
                },
              },
            },
          },
        },
      },
    },
    branches: {
      type: 'array',
      title: '分支时间线',
      description: '可能的分支时间线',
      items: {
        type: 'object',
        required: ['branch_id', 'condition'],
        properties: {
          branch_id: {
            type: 'string',
            title: '分支 ID',
            pattern: '^timeline_[a-zA-Z0-9_]+$',
          },
          branch_name: {
            type: 'string',
            title: '分支名称',
            maxLength: 100,
          },
          condition: {
            type: 'string',
            title: '分支条件',
            description: '进入分支的条件表达式',
          },
          priority: {
            type: 'integer',
            title: '优先级',
            minimum: 0,
            maximum: 100,
            default: 50,
          },
        },
      },
    },
    loop: {
      type: 'boolean',
      title: '循环',
      description: '是否循环播放时间线',
      default: false,
    },
    auto_advance: {
      type: 'boolean',
      title: '自动推进',
      description: '是否自动推进时间线',
      default: true,
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type TimelineConfig = {
  timeline_id: string
  name: string
  description?: string
  events?: Array<{
    event_id: string
    event_name?: string
    trigger_time: number
    event_type?: string
    target?: string
    condition?: string
    duration?: number
    priority?: number
    skippable?: boolean
    effects?: Array<{
      effect_type?: string
      target?: string
      value?: string | number | boolean | object
    }>
  }>
  branches?: Array<{
    branch_id: string
    branch_name?: string
    condition: string
    priority?: number
  }>
  loop?: boolean
  auto_advance?: boolean
  metadata?: Record<string, any>
}
